/**
 * PackSelector — BESC Draw
 *
 * Triumph/Rips-style layout:
 *  - 3D CSS pack bag as the hero (no external images needed)
 *  - Horizontal carousel to switch between tiers
 *  - "What's Inside" bottom sheet: Recent Wins + Grails from live pool
 *  - Odds modal with Normal/High volatility toggle
 *  - Big bottom CTA with token selector
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { UsePokemonTCGResult, LiveCard } from "../hooks/usePokemonTCG";
import { buildTierPool } from "../hooks/usePackDraw";
import { RARITY_LABEL, RARITY_COLOR } from "./PokemonCard";
import { SlabView } from "./SlabView";
// Helper: format a raw 18-dec on-chain pack price into a token-symbol label.
//   BUSDC/GEM         → integer like "10 BUSDC", "1,000 GEM"
//   WBNB/WETH/BESC    → 4-decimal float like "0.0167 WBNB"
function formatPackPrice(raw: bigint | undefined, tokenIdx: number): string {
  if (raw == null) return "…";
  const decimals = 18;
  const whole = raw / 10n ** BigInt(decimals);
  const frac  = raw % 10n ** BigInt(decimals);
  const num   = Number(whole) + Number(frac) / 1e18;
  const sym   = ["BUSDC", "WBNB", "WETH", "BESC", "GEM"][tokenIdx] ?? "TOKEN";
  if (tokenIdx === 0 || tokenIdx === 4) {
    return `${Math.round(num).toLocaleString()} ${sym}`;
  }
  return `${num.toFixed(num < 0.01 ? 5 : 4)} ${sym}`;
}
import "./PackSelector.scss";

// ── Payment tokens ────────────────────────────────────────────────────────────
export const TOKENS = [
  { id: 0, symbol: "BUSDC", label: "BUSDC",   icon: "💵" },
  { id: 1, symbol: "WBNB",  label: "WBNB",    icon: "🟡" },
  { id: 2, symbol: "WETH",  label: "WETH",    icon: "💠" },
  { id: 3, symbol: "BESC",  label: "BESC",    icon: "🔵" },
  { id: 4, symbol: "GEM",   label: "GEMS",    icon: "💎" },
] as const;

export const TOKEN_SYMBOLS  = { 0: "BUSDC", 1: "WBNB", 2: "WETH", 3: "BESC", 4: "GEM" } as Record<number,string>;
export const TOKEN_DECIMALS = { 0: 18, 1: 18, 2: 18, 3: 18, 4: 18 } as Record<number,number>;

// ── Pack tiers — $3 to $1,000 per pull ───────────────────────────────────────
export const TIER_DATA = [
  {
    id:          0,
    name:        "Mini Pack",
    subtitle:    "Pokemon Mini Pack",
    emoji:       "⚡",
    cards:       10,
    desc:        "Pick 1 card from a pool of 10. Entry-level — commons & uncommons, occasional rare. Max pull $30.",
    bgTop:       "#0d1a0f",
    bgMid:       "#091209",
    bgBot:       "#050a05",
    foil:        "rgba(74,222,128,0.3)",
    stripeColor: "#4ade80",
    crimpColor:  "#16a34a",
    accent:      "#4ade80",
    minValue:    "$0.02",
    maxPull:     "$30",
    prices:      { 0: "$3",  1: "0.010", 2: "0.0012", 3: "3",  4: "300"   } as Record<number,string>,
    priceLabels: { 0: "3 BUSDC", 1: "0.010 WBNB", 2: "0.0012 WETH", 3: "3 BESC", 4: "300 GEM" } as Record<number,string>,
    priceRaw:    { 0: "3000000000000000000", 1: "10000000000000000", 2: "1200000000000000", 3: "3000000000000000000", 4: "300000000000000000000" } as Record<number,string>,
    featured:    false,
    odds: {
      normal: { common: "62%", uncommon: "26%", rare: "9%",  holo: "2.5%", ultra: "0.5%", graded: "0%"   },
      high:   { common: "46%", uncommon: "22%", rare: "16%", holo: "11%",  ultra: "4%",   graded: "1%"   },
    },
    previewSeed: "0x1111111111111111111111111111111111111111",
    bgGradient:  "linear-gradient(160deg, #064e3b 0%, #047857 55%, #6ee7b7 100%)",
    foilColor:   "rgba(74,222,128,0.4)",
  },
  {
    id:          1,
    name:        "Starter Pack",
    subtitle:    "Pokemon Starter Pack",
    emoji:       "🔵",
    cards:       15,
    desc:        "Pick 1 card from a pool of 15. Better rare odds than Mini — holos possible. Max pull $100.",
    bgTop:       "#0f1e3d",
    bgMid:       "#0b1528",
    bgBot:       "#070e18",
    foil:        "rgba(96,165,250,0.35)",
    stripeColor: "#3b82f6",
    crimpColor:  "#c0892a",
    accent:      "#3b82f6",
    minValue:    "$0.05",
    maxPull:     "$100",
    prices:      { 0: "$10", 1: "0.033", 2: "0.004", 3: "10", 4: "1,000" } as Record<number,string>,
    priceLabels: { 0: "10 BUSDC", 1: "0.033 WBNB", 2: "0.004 WETH", 3: "10 BESC", 4: "1000 GEM" } as Record<number,string>,
    priceRaw:    { 0: "10000000000000000000", 1: "33000000000000000", 2: "4000000000000000", 3: "10000000000000000000", 4: "1000000000000000000000" } as Record<number,string>,
    featured:    false,
    odds: {
      normal: { common: "52%", uncommon: "30%", rare: "12%", holo: "4%",  ultra: "2%",  graded: "0%"   },
      high:   { common: "35%", uncommon: "25%", rare: "21%", holo: "14%", ultra: "4%",  graded: "1%"   },
    },
    previewSeed: "0xabcdef1234567890abcdef1234567890abcdef12",
    bgGradient:  "linear-gradient(160deg, #0f2460 0%, #1e56b0 55%, #5ba4f5 100%)",
    foilColor:   "rgba(91,164,245,0.45)",
  },
  {
    id:          2,
    name:        "Booster Pack",
    subtitle:    "Pokemon Booster Pack",
    emoji:       "🔥",
    cards:       20,
    desc:        "Pick 1 card from a pool of 20. Solid holo & ultra rare odds. Max pull $500.",
    bgTop:       "#1a0f2e",
    bgMid:       "#120a20",
    bgBot:       "#0a0612",
    foil:        "rgba(192,132,252,0.35)",
    stripeColor: "#a855f7",
    crimpColor:  "#8b5cf6",
    accent:      "#a855f7",
    minValue:    "$0.10",
    maxPull:     "$500",
    prices:      { 0: "$50", 1: "0.167", 2: "0.020", 3: "50", 4: "5,000" } as Record<number,string>,
    priceLabels: { 0: "50 BUSDC", 1: "0.167 WBNB", 2: "0.020 WETH", 3: "50 BESC", 4: "5000 GEM" } as Record<number,string>,
    priceRaw:    { 0: "50000000000000000000", 1: "167000000000000000", 2: "20000000000000000", 3: "50000000000000000000", 4: "5000000000000000000000" } as Record<number,string>,
    featured:    false,
    odds: {
      normal: { common: "42%", uncommon: "28%", rare: "20%", holo: "7%",  ultra: "3%",  graded: "0%"   },
      high:   { common: "26%", uncommon: "20%", rare: "28%", holo: "18%", ultra: "6%",  graded: "2%"   },
    },
    previewSeed: "0x1234567890abcdef1234567890abcdef12345678",
    bgGradient:  "linear-gradient(160deg, #3b0764 0%, #7e22ce 55%, #c084fc 100%)",
    foilColor:   "rgba(192,132,252,0.45)",
  },
  {
    id:          3,
    name:        "Pro Pack",
    subtitle:    "Pokemon Pro Pack",
    emoji:       "🎯",
    cards:       25,
    desc:        "Pick 1 card from a pool of 25. Strong rare/holo pool, real ultra rate. Max pull $1,000.",
    bgTop:       "#1c0d00",
    bgMid:       "#120800",
    bgBot:       "#080400",
    foil:        "rgba(249,115,22,0.35)",
    stripeColor: "#f97316",
    crimpColor:  "#ea580c",
    accent:      "#f97316",
    minValue:    "$0.25",
    maxPull:     "$1,000",
    prices:      { 0: "$100", 1: "0.333", 2: "0.040", 3: "100", 4: "10,000" } as Record<number,string>,
    priceLabels: { 0: "100 BUSDC", 1: "0.333 WBNB", 2: "0.040 WETH", 3: "100 BESC", 4: "10000 GEM" } as Record<number,string>,
    priceRaw:    { 0: "100000000000000000000", 1: "333000000000000000", 2: "40000000000000000", 3: "100000000000000000000", 4: "10000000000000000000000" } as Record<number,string>,
    featured:    false,
    odds: {
      normal: { common: "35%", uncommon: "28%", rare: "24%", holo: "9%",  ultra: "4%",  graded: "0%"  },
      high:   { common: "18%", uncommon: "20%", rare: "32%", holo: "22%", ultra: "7%",  graded: "1%"  },
    },
    previewSeed: "0xabcd1234ef567890abcd1234ef567890abcd1234",
    bgGradient:  "linear-gradient(160deg, #431407 0%, #c2410c 55%, #fed7aa 100%)",
    foilColor:   "rgba(249,115,22,0.45)",
  },
  {
    id:          4,
    name:        "Elite Pack",
    subtitle:    "Pokemon Elite Pack",
    emoji:       "⭐",
    cards:       30,
    desc:        "Pick 1 card from a pool of 30. Graded slabs possible. Holos & ultras flow freely. Max pull $2,500.",
    bgTop:       "#1c1000",
    bgMid:       "#120b00",
    bgBot:       "#080500",
    foil:        "rgba(251,191,36,0.4)",
    stripeColor: "#f59e0b",
    crimpColor:  "#d97706",
    accent:      "#f59e0b",
    minValue:    "$0.50",
    maxPull:     "$2,500",
    prices:      { 0: "$250", 1: "0.833", 2: "0.100", 3: "250", 4: "25,000" } as Record<number,string>,
    priceLabels: { 0: "250 BUSDC", 1: "0.833 WBNB", 2: "0.100 WETH", 3: "250 BESC", 4: "25000 GEM" } as Record<number,string>,
    priceRaw:    { 0: "250000000000000000000", 1: "833000000000000000", 2: "100000000000000000", 3: "250000000000000000000", 4: "25000000000000000000000" } as Record<number,string>,
    featured:    true,
    odds: {
      normal: { common: "27%", uncommon: "28%", rare: "27%", holo: "13%", ultra: "4.5%", graded: "0.5%" },
      high:   { common: "10%", uncommon: "16%", rare: "32%", holo: "28%", ultra: "10%",  graded: "4%"   },
    },
    previewSeed: "0x9876543210fedcba9876543210fedcba98765432",
    bgGradient:  "linear-gradient(160deg, #451a03 0%, #b45309 55%, #fde68a 100%)",
    foilColor:   "rgba(251,191,36,0.5)",
  },
  {
    id:          5,
    name:        "Champion Pack",
    subtitle:    "Pokemon Champion Pack",
    emoji:       "🏆",
    cards:       40,
    desc:        "Pick 1 card from a pool of 40. Premium ultra & graded rate. PSA slabs possible. Max pull $5,000.",
    bgTop:       "#001a1a",
    bgMid:       "#00110f",
    bgBot:       "#000a08",
    foil:        "rgba(20,184,166,0.35)",
    stripeColor: "#14b8a6",
    crimpColor:  "#0d9488",
    accent:      "#14b8a6",
    minValue:    "$1.00",
    maxPull:     "$5,000",
    prices:      { 0: "$500", 1: "1.667", 2: "0.200", 3: "500", 4: "50,000" } as Record<number,string>,
    priceLabels: { 0: "500 BUSDC", 1: "1.667 WBNB", 2: "0.200 WETH", 3: "500 BESC", 4: "50000 GEM" } as Record<number,string>,
    priceRaw:    { 0: "500000000000000000000", 1: "1667000000000000000", 2: "200000000000000000", 3: "500000000000000000000", 4: "50000000000000000000000" } as Record<number,string>,
    featured:    true,
    odds: {
      normal: { common: "19%", uncommon: "26%", rare: "30%", holo: "18%", ultra: "6%",  graded: "1%"   },
      high:   { common: "6%",  uncommon: "10%", rare: "32%", holo: "34%", ultra: "13%", graded: "5%"   },
    },
    previewSeed: "0xcafe1234cafe5678cafe9abccafe1234cafe5678",
    bgGradient:  "linear-gradient(160deg, #042f2e 0%, #0f766e 55%, #5eead4 100%)",
    foilColor:   "rgba(20,184,166,0.45)",
  },
  {
    id:          6,
    name:        "Legendary Box",
    subtitle:    "Pokemon Legendary Box",
    emoji:       "💎",
    cards:       50,
    desc:        "Pick 1 card from a pool of 50. Top graded slabs. Best ultra & holo rate on the platform. Max pull $10,000.",
    bgTop:       "#1a0000",
    bgMid:       "#110000",
    bgBot:       "#080000",
    foil:        "rgba(248,113,113,0.35)",
    stripeColor: "#ef4444",
    crimpColor:  "#dc2626",
    accent:      "#ef4444",
    minValue:    "$2.00",
    maxPull:     "$10,000",
    prices:      { 0: "$1,000", 1: "3.333", 2: "0.400", 3: "1000", 4: "100,000" } as Record<number,string>,
    priceLabels: { 0: "1000 BUSDC", 1: "3.333 WBNB", 2: "0.400 WETH", 3: "1000 BESC", 4: "100000 GEM" } as Record<number,string>,
    priceRaw:    { 0: "1000000000000000000000", 1: "3333000000000000000", 2: "400000000000000000", 3: "1000000000000000000000", 4: "100000000000000000000000" } as Record<number,string>,
    featured:    false,
    odds: {
      normal: { common: "12%", uncommon: "25%", rare: "32%", holo: "22%", ultra: "7.5%", graded: "1.5%" },
      high:   { common: "3%",  uncommon: "7%",  rare: "28%", holo: "38%", ultra: "16%",  graded: "8%"   },
    },
    previewSeed: "0xdeadbeefdeadbeefdeadbeefdeadbeefdeadbeef",
    bgGradient:  "linear-gradient(160deg, #450a0a 0%, #b91c1c 55%, #fca5a5 100%)",
    foilColor:   "rgba(248,113,113,0.5)",
  },
];

// ── Props ─────────────────────────────────────────────────────────────────────
interface Props {
  selectedTier:       number;
  selectedToken:      number;
  tcg:                UsePokemonTCGResult;
  gemBalance:         bigint;
  packPrices:         (bigint | undefined)[];   // length 5, raw 18-dec, current tier
  usdPrice:           number;                   // USD label for the current tier
  onTierChange:       (tier: number) => void;
  onTokenChange:      (token: number) => void;
  onOpen:             () => void;
  isOpening:          boolean;
  needsApproval:      boolean;
  hasBalance:         boolean;
  isApproving:        boolean;
  onApprove:          () => void;
  account:            string | undefined;
  volatility:         "normal" | "high";
  onVolatilityChange: (v: "normal" | "high") => void;
  // v4 features
  packCount:            number;
  onPackCountChange:    (n: number) => void;
  giftMode:             boolean;
  onGiftModeChange:     (v: boolean) => void;
  giftRecipient:        string;
  onGiftRecipientChange:(v: string) => void;
  promoCode:            string;
  onPromoCodeChange:    (v: string) => void;
  promoDiscount:        number;  // 0 = no discount, >0 = active (basis points)
  tierSoldOut:          boolean;
  tierSupplyUsed?:      number;
  tierMaxSupply?:       number;
  jackpotInterval?:     number;
  packCount_global?:    number; // total packs opened globally
  demo?:                boolean; // off-chain demo: no wallet, no payment
}

const BATCH_OPTIONS = [1, 3, 5, 10, 20] as const;

export function PackSelector({
  demo = false,
  selectedTier, selectedToken, tcg, gemBalance,
  packPrices, usdPrice,
  onTierChange, onTokenChange, onOpen,
  isOpening, needsApproval, hasBalance,
  isApproving, onApprove, account,
  volatility, onVolatilityChange,
  packCount, onPackCountChange,
  giftMode, onGiftModeChange,
  giftRecipient, onGiftRecipientChange,
  promoCode, onPromoCodeChange,
  promoDiscount, tierSoldOut,
  tierSupplyUsed, tierMaxSupply,
  jackpotInterval, packCount_global,
}: Props) {
  const [showWhatsInside, setShowWhatsInside] = useState(false);
  const [showOdds,        setShowOdds]        = useState(false);
  const [winDetailCard,   setWinDetailCard]   = useState<LiveCard | null>(null);

  const [showTokens,      setShowTokens]      = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const tier = TIER_DATA[selectedTier];
  const currentOdds = tier.odds[volatility];
  // usdPrice is now a prop (driven by contract oracle + tier face value)

  // Build a tier-appropriate sub-pool (value caps + grail caps per tier).
  // Different tiers now preview genuinely different cards.
  const tierPool = tcg.pool ? buildTierPool(tcg.pool, selectedTier) : null;

  // "What Can Drop" — show the MOST EXCITING possible pulls, not random commons.
  // Pull top ultras first, then holos, then rares (all value-sorted desc).
  // Tier value floor ensures $3 pack shows $5-30 cards, $1000 shows $200+ cards.
  const TIER_PREVIEW_FLOOR = [2, 5, 20, 40, 80, 150, 350] as const;
  const sampleCards: LiveCard[] = (() => {
    if (!tierPool) return [];
    const floor   = TIER_PREVIEW_FLOOR[Math.min(selectedTier, TIER_PREVIEW_FLOOR.length - 1)];
    const sortDesc = (arr: LiveCard[]) => [...arr].sort((a,b) => b.approxValueUsd - a.approxValueUsd);
    const ultras  = sortDesc(tierPool.ultra  ?? []).filter(c => c.approxValueUsd >= floor).slice(0, 4);
    const graded  = sortDesc(tierPool.graded ?? []).filter(c => c.approxValueUsd >= floor).slice(0, 2);
    const holos   = sortDesc(tierPool.holo   ?? []).filter(c => c.approxValueUsd >= floor).slice(0, 5);
    const rares   = sortDesc(tierPool.rare   ?? []).filter(c => c.approxValueUsd >= floor).slice(0, 3);
    // Ultras + graded first for max hype, then holos, then rares
    const combined = [...graded, ...ultras, ...holos, ...rares].filter(c => c.approxValueUsd > 0);
    // Deduplicate by id/name
    const seen = new Set<string>();
    return combined.filter(c => { const k = c.id ?? c.name; return seen.has(k) ? false : (seen.add(k), true); });
  })();

  // Grail cards — already filtered by TIER_GRAIL_CAPS inside buildTierPool
  const gradedCards = (tierPool?.graded ?? [])
    .sort((a, b) => b.approxValueUsd - a.approxValueUsd);

  const scrollCarousel = useCallback((dir: -1 | 1) => {
    const next = Math.max(0, Math.min(TIER_DATA.length - 1, selectedTier + dir));
    onTierChange(next);
  }, [selectedTier, onTierChange]);

  // Scroll selected item into center of the track (mobile snap-scroll)
  useEffect(() => {
    const track = carouselRef.current;
    if (!track) return;
    const items = track.querySelectorAll<HTMLElement>(".PS__carousel-item");
    const el = items[selectedTier];
    if (!el) return;
    const trackRect = track.getBoundingClientRect();
    const itemRect  = el.getBoundingClientRect();
    const scrollTo  = track.scrollLeft + itemRect.left - trackRect.left
                      - (trackRect.width - itemRect.width) / 2;
    track.scrollTo({ left: scrollTo, behavior: "smooth" });
  }, [selectedTier]);

  return (
    <div className="PS" style={{ "--tier-accent": tier.accent } as React.CSSProperties}>

      {/* ── What's Inside bottom sheet ── */}
      <AnimatePresence>
        {showWhatsInside && (
          <motion.div className="PS__sheet-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowWhatsInside(false)}
          >
            <motion.div className="PS__sheet"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="PS__sheet-handle" />

              {/* Header */}
              <div className="PS__sheet-head">
                <PackIcon tier={tier} size={40} />
                <div>
                  <p className="PS__sheet-sub">{tier.subtitle}</p>
                  <h2 className="PS__sheet-title">What's Inside</h2>
                </div>
                <button className="PS__sheet-close" onClick={() => setShowWhatsInside(false)}>✕</button>
              </div>

              {/* Max pull hype bar */}
              <div className="PS__max-pull-banner" style={{ borderColor: `${tier.accent}44`, background: `${tier.accent}0d` }}>
                <span className="PS__max-pull-label">Max Single Pull</span>
                <span className="PS__max-pull-value" style={{ color: tier.accent }}>{tier.maxPull}</span>
                <span className="PS__max-pull-sep">·</span>
                <span className="PS__max-pull-label">Pick 1 Card</span>
              </div>

              {/* Guarantee banner */}
              <div className="PS__guarantee">
                <span className="PS__guarantee-icon">♻️</span>
                <div>
                  <strong className="PS__guarantee-title">7-Day Buyback Guarantee</strong>
                  <p className="PS__guarantee-body">
                    Not happy with your pull? Sell your card back on-chain within 7 days at its full market value.
                    Get <strong>100%</strong> of card value in any token, or <strong>75%</strong> in GEM.
                    Fully automated — instant settlement, zero questions.
                  </p>
                  <div className="PS__guarantee-rates">
                    <div className="PS__guarantee-rate">
                      <span className="PS__guarantee-rate-pct">50%</span>
                      <span className="PS__guarantee-rate-label">back in crypto</span>
                      <span className="PS__guarantee-rate-sub">BUSDC, WBNB, WETH, or BESC</span>
                    </div>
                    <div className="PS__guarantee-rate-divider">or</div>
                    <div className="PS__guarantee-rate gem">
                      <span className="PS__guarantee-rate-pct">75%</span>
                      <span className="PS__guarantee-rate-label">back in GEM</span>
                      <span className="PS__guarantee-rate-sub">Spend GEM on future packs (100 GEM = $1)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rarity odds bar */}
              <div className="PS__inside-odds">
                {Object.entries(currentOdds).map(([rarity, pct]) => {
                  const numericPct = parseFloat(pct);
                  return (
                    <div key={rarity} className="PS__inside-odds-row">
                      <div className="PS__inside-odds-label">
                        <span style={{ color: RARITY_COLOR[rarity as any] }}>{RARITY_LABEL[rarity as any] ?? rarity}</span>
                      </div>
                      <div className="PS__inside-odds-bar-wrap">
                        <motion.div
                          className="PS__inside-odds-bar"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(numericPct * 1.2, 100)}%` }}
                          transition={{ duration: 0.5, delay: 0.05 }}
                          style={{ background: RARITY_COLOR[rarity as any] }}
                        />
                      </div>
                      <span className="PS__inside-odds-pct">{pct}</span>
                    </div>
                  );
                })}
              </div>

              {/* What Can Drop — high-value pulls only, sorted by value */}
              <div className="PS__section-head">
                <h3 className="PS__section-title">🔥 What Can Drop</h3>
                <span className="PS__section-range">up to {tier.maxPull}</span>
              </div>
              <div className="PS__wins-rule" />
              {tcg.loading ? (
                <p className="PS__loading">Loading card pool…</p>
              ) : sampleCards.length === 0 ? (
                <p className="PS__loading">No cards loaded yet — check back shortly.</p>
              ) : (
                <div className="PS__wins-scroll">
                  {sampleCards.slice(0, 12).map((card, i) => (
                    <div key={i} className={`PS__win-card${card.rarity === "ultra" || card.rarity === "graded" ? " hype" : ""}`}
                      onClick={() => setWinDetailCard(card)}>
                      <div className={`PS__win-img-wrap${card.isGraded ? " graded" : card.rarity === "ultra" ? " ultra" : ""}`}>
                        <img src={card.imageLarge ?? card.imageSmall} alt={card.name} className="PS__win-img"
                          onError={(e) => {
                            const img = e.currentTarget as HTMLImageElement;
                            if (card.imageSmall && img.src !== card.imageSmall) { img.src = card.imageSmall; }
                            else { img.style.opacity = "0"; }
                          }} />
                        {card.isGraded && (
                          <div className="PS__win-grade">{card.gradingCompany} {card.grade}</div>
                        )}
                        {card.isAltArt && !card.isGraded && (
                          <div className="PS__win-altart">Alt Art</div>
                        )}
                      </div>
                      <p className="PS__win-value"
                        style={{ color: card.rarity === "graded" ? "#ffd700" : RARITY_COLOR[card.rarity as any] ?? "#4ade80",
                                 fontSize: card.approxValueUsd >= 100 ? "0.88rem" : "0.78rem",
                                 fontWeight: card.approxValueUsd >= 100 ? 900 : 800 }}>
                        ${card.approxValueUsd >= 1000
                          ? (card.approxValueUsd / 1000).toFixed(1) + "k"
                          : card.approxValueUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </p>
                      <p className="PS__win-rarity" style={{ color: RARITY_COLOR[card.rarity as any] }}>
                        {card.rarity === "graded" ? `${card.gradingCompany ?? "PSA"} ${card.grade ?? "10"}` : (RARITY_LABEL[card.rarity as any] ?? card.rarity)}
                      </p>
                      <p className="PS__win-name">{card.name}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Grails — authenticated graded slabs you can pull */}
              {gradedCards.length > 0 && (
                <>
                  <div className="PS__section-head" style={{ marginTop: "1.75rem" }}>
                    <h3 className="PS__section-title">🏆 Graded Slabs</h3>
                    <span className="PS__section-range">PSA · BGS · CGC authenticated</span>
                  </div>
                  <div className="PS__wins-rule" />
                  <div className="PS__wins-scroll">
                    {gradedCards.slice(0, 12).map((card, i) => (
                      <div key={i} className="PS__win-card hype" onClick={() => setWinDetailCard(card)}>
                        <div className="PS__win-img-wrap graded">
                          <img src={card.imageLarge ?? card.imageSmall} alt={card.name} className="PS__win-img"
                            onError={(e) => {
                              const img = e.currentTarget as HTMLImageElement;
                              if (card.imageSmall && img.src !== card.imageSmall) { img.src = card.imageSmall; }
                              else { img.style.opacity = "0"; }
                            }} />
                          <div className="PS__win-grade">{card.gradingCompany} {card.grade}</div>
                        </div>
                        <p className="PS__win-value" style={{ color: "#ffd700", fontSize:"0.88rem", fontWeight:900 }}>
                          ${card.approxValueUsd >= 1000
                            ? (card.approxValueUsd / 1000).toFixed(1) + "k"
                            : card.approxValueUsd.toLocaleString()}
                        </p>
                        <p className="PS__win-rarity" style={{ color: "#ffd700" }}>Graded</p>
                        <p className="PS__win-name">{card.name}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div style={{ height: "2.5rem" }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Card detail — full-screen SlabView ── */}
      <AnimatePresence>
        {winDetailCard && (
          <SlabView
            card={winDetailCard}
            onClose={() => setWinDetailCard(null)}
            hint={`This card can drop in the ${tier.name}. Open a pack for a chance to pull it.`}
          />
        )}
      </AnimatePresence>

      {/* ── Odds modal ── */}
      <AnimatePresence>
        {showOdds && (
          <motion.div className="PS__sheet-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowOdds(false)}
          >
            <motion.div className="PS__sheet"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="PS__sheet-handle" />

              <div className="PS__sheet-head">
                <PackIcon tier={tier} size={40} />
                <div>
                  <p className="PS__sheet-sub">{tier.subtitle}</p>
                  <h2 className="PS__sheet-title">Estimated Odds &amp; Values</h2>
                </div>
                <button className="PS__sheet-close" onClick={() => setShowOdds(false)}>✕</button>
              </div>

              <p className="PS__odds-sub">Choose your volatility level</p>
              <div className="PS__vol-toggle">
                <button className={`PS__vol-btn${volatility === "normal" ? " active" : ""}`} onClick={() => onVolatilityChange("normal")}>Normal</button>
                <button className={`PS__vol-btn${volatility === "high"   ? " active" : ""}`} onClick={() => onVolatilityChange("high")}>High</button>
              </div>

              <div className="PS__odds-bars">
                {Object.entries(currentOdds).map(([rarity, pct], i) => {
                  const numericPct = parseFloat(pct);
                  const valueRanges: Record<string, string> = {
                    common: "$0.01–$1", uncommon: "$0.50–$3", rare: "$1–$8",
                    holo: "$3–$25", ultra: "$15–$200", graded: "$80–$50,000+",
                  };
                  return (
                    <div key={rarity} className="PS__odds-bar-row">
                      <div className="PS__odds-bar-label">
                        <span>{RARITY_LABEL[rarity as any] ?? rarity}</span>
                        <span className="PS__odds-bar-range">{valueRanges[rarity] ?? ""}</span>
                      </div>
                      <div className="PS__odds-bar-track">
                        <motion.div
                          className="PS__odds-bar-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(numericPct * 1.1, 100)}%` }}
                          transition={{ duration: 0.55, delay: i * 0.06 }}
                          style={{ background: RARITY_COLOR[rarity as any] }}
                        />
                        <span className="PS__odds-bar-pct">{pct}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="PS__odds-footer">
                <div>
                  <span className="PS__odds-footer-label">Min Value</span>
                  <span className="PS__odds-footer-val">{tier.minValue}</span>
                </div>
                <div className="PS__odds-footer-div" />
                <div>
                  <span className="PS__odds-footer-label">Max Pull</span>
                  <span className="PS__odds-footer-val">{tier.maxPull}</span>
                </div>
              </div>

              <p className="PS__odds-disclaimer">
                Cards can be sold back within 7 days at full market value. Odds are calculated at time of purchase and subject to change.
              </p>

              <button className="PS__apply-btn" onClick={() => setShowOdds(false)}>Apply</button>
              <div style={{ height: "env(safe-area-inset-bottom, 1rem)" }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Token selector sheet ── */}
      <AnimatePresence>
        {showTokens && (
          <motion.div className="PS__sheet-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowTokens(false)}
          >
            <motion.div className="PS__sheet PS__sheet--short"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="PS__sheet-handle" />
              <div className="PS__token-sheet-head">
                <h2 className="PS__token-sheet-title">Pay With</h2>
                <p className="PS__token-sheet-sub">≈ ${usdPrice.toLocaleString()} USD</p>
              </div>
              <div className="PS__token-list">
                {TOKENS.map((t) => {
                  const isActive = selectedToken === t.id;
                  const priceLabel = formatPackPrice(packPrices[t.id], t.id);
                  const isLive = t.id === 1 || t.id === 2 || t.id === 3;
                  return (
                    <button
                      key={t.id}
                      className={`PS__token-row${isActive ? " active" : ""}`}
                      onClick={() => { onTokenChange(t.id); setShowTokens(false); }}
                    >
                      <span className="PS__token-row-icon">{t.icon}</span>
                      <span className="PS__token-row-text">
                        <span className="PS__token-row-label">
                          {t.label}
                          {isLive && <span className="PS__token-row-live">live</span>}
                        </span>
                        <span className="PS__token-row-price">{priceLabel}</span>
                      </span>
                      {t.id === 4 && account && (
                        <span className="PS__token-row-bal">
                          {Number(gemBalance / BigInt(1e18)).toLocaleString()} GEM
                        </span>
                      )}
                      {isActive
                        ? <span className="PS__token-row-check">✓</span>
                        : <span className="PS__token-row-arrow">›</span>}
                    </button>
                  );
                })}
              </div>
              <div style={{ height: "env(safe-area-inset-bottom, 1rem)" }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Tier picker strip ── */}
      <div className="PS__tier-strip">
        {TIER_DATA.map((t) => (
          <button
            key={t.id}
            className={`PS__tier-pill${t.id === selectedTier ? " active" : ""}`}
            style={t.id === selectedTier
              ? { "--pill-color": t.accent } as React.CSSProperties
              : undefined}
            onClick={() => onTierChange(t.id)}
          >
            <span className="PS__tier-pill-emoji">{t.emoji}</span>
            <span className="PS__tier-pill-name">{t.name.split(" ")[0]}</span>
            <span className="PS__tier-pill-price">{t.prices[0]}</span>
          </button>
        ))}
      </div>

      {/* ══════ MAIN PACK HERO ══════ */}
      <div className="PS__hero">

        {/* Live pool dot */}
        <div className="PS__pool-status">
          {tcg.loading ? (
            <><span className="PS__dot" /> Loading card pool…</>
          ) : (
            <><span className="PS__dot alive" /> {tcg.totalCards.toLocaleString()} real cards loaded</>
          )}
        </div>

        {/* Horizontal pack carousel */}
        <div className="PS__carousel">
          <button className="PS__carousel-arrow left" onClick={() => scrollCarousel(-1)} disabled={selectedTier === 0}>‹</button>

          <div className="PS__carousel-track" ref={carouselRef}>
            {TIER_DATA.map((t) => {
              const isSelected = t.id === selectedTier;
              return (
                <motion.div
                  key={t.id}
                  className={`PS__carousel-item${isSelected ? " selected" : ""}`}
                  animate={{ scale: isSelected ? 1 : 0.78, opacity: isSelected ? 1 : 0.45 }}
                  transition={{ type: "spring", stiffness: 260, damping: 24 }}
                  onClick={() => onTierChange(t.id)}
                >
                  <Pack3D tier={t} active={isSelected} />
                </motion.div>
              );
            })}
          </div>

          <button className="PS__carousel-arrow right" onClick={() => scrollCarousel(1)} disabled={selectedTier === TIER_DATA.length - 1}>›</button>
        </div>

        {/* Pack name + What's Inside */}
        <motion.div
          className="PS__pack-info"
          key={selectedTier}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <h2 className="PS__pack-name">{tier.subtitle}</h2>

          {/* ── Price ── */}
          <div className="PS__price-row">
            <span className="PS__price-amount" style={{ color: tier.accent }}>
              {formatPackPrice(packPrices[selectedToken], selectedToken)}
            </span>
            <span className="PS__price-sep">·</span>
            <span className="PS__price-cards">Pick 1 card</span>
          </div>

          <button className="PS__whats-inside-btn" onClick={() => setShowWhatsInside(true)}>
            <span>👁</span> What's inside
          </button>
        </motion.div>

        {/* Stats row */}
        <div className="PS__stats-row">
          <div className="PS__stat">
            <span className="PS__stat-label">Min Value</span>
            <span className="PS__stat-val">{tier.minValue}</span>
          </div>
          <div className="PS__stat center">
            <span className="PS__stat-label">Max Pull</span>
            <span className="PS__stat-val big">{tier.maxPull}</span>
          </div>
          <button className="PS__stat clickable" onClick={() => setShowOdds(true)}>
            <span className="PS__stat-label">Adjust Odds</span>
            <span className="PS__stat-val cta">{volatility === "high" ? "High" : "Normal"} ›</span>
          </button>
        </div>

        {/* Tier dots */}
        <div className="PS__tier-dots">
          {TIER_DATA.map((t) => (
            <button
              key={t.id}
              className={`PS__tier-dot${t.id === selectedTier ? " active" : ""}`}
              style={{ "--dot-color": t.accent } as React.CSSProperties}
              onClick={() => onTierChange(t.id)}
            />
          ))}
        </div>
      </div>

      {/* ══════ CONTROLS PANEL (scrollable, above sticky CTA) ══════ */}
      <div className="PS__controls-panel">

        {/* Jackpot + supply info row */}
        {((jackpotInterval && jackpotInterval > 0 && packCount_global != null) ||
          (tierMaxSupply != null && tierMaxSupply > 0 && tierSupplyUsed != null)) && (
          <div className="PS__info-pills-row">
            {jackpotInterval && jackpotInterval > 0 && packCount_global != null && (
              <div className="PS__info-pill jackpot" style={{ borderColor: `${tier.accent}35`, color: tier.accent }}>
                🎰 Jackpot: pack #{Math.ceil((packCount_global + 1) / jackpotInterval) * jackpotInterval}
                <span className="PS__info-pill-sub">({jackpotInterval - (packCount_global % jackpotInterval)} away)</span>
              </div>
            )}
            {tierMaxSupply != null && tierMaxSupply > 0 && tierSupplyUsed != null && (
              <div className={`PS__info-pill supply${tierSoldOut ? " sold-out" : ""}`}>
                {tierSoldOut ? "🚫 SOLD OUT" : `📦 ${tierSupplyUsed.toLocaleString()}/${tierMaxSupply.toLocaleString()} opened`}
              </div>
            )}
          </div>
        )}

        {/* ── Quantity selector ── */}
        <div className="PS__qty-section">
          <div className="PS__qty-header">
            <span className="PS__qty-label">Quantity</span>
            {packCount > 1 && packPrices[selectedToken] != null && (
              <span className="PS__qty-total" style={{ color: tier.accent }}>
                {formatPackPrice(packPrices[selectedToken]! * BigInt(packCount), selectedToken)}
                <span className="PS__qty-total-usd">
                  {" "}≈ ${(usdPrice * packCount * (promoDiscount > 0 ? (10000 - promoDiscount) / 10000 : 1)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </span>
            )}
          </div>
          <div className="PS__qty-pills">
            {BATCH_OPTIONS.map((n) => {
              const totalForN = packPrices[selectedToken] != null
                ? formatPackPrice(packPrices[selectedToken]! * BigInt(n), selectedToken)
                : null;
              return (
                <motion.button
                  key={n}
                  className={`PS__qty-pill${packCount === n ? " active" : ""}`}
                  style={packCount === n ? { "--pill-color": tier.accent, "--pill-glow": tier.foilColor } as React.CSSProperties : undefined}
                  onClick={() => onPackCountChange(n)}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.94 }}
                >
                  <span className="PS__qty-pill-num">{n === 1 ? "1" : `×${n}`}</span>
                  {n > 1 && totalForN && (
                    <span className="PS__qty-pill-cost">{totalForN}</span>
                  )}
                  {n === 1 && (
                    <span className="PS__qty-pill-cost">{formatPackPrice(packPrices[selectedToken], selectedToken)}</span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* HV mode notice */}
        {volatility === "high" && (
          <div className="PS__hv-notice">
            <span>⚡ High Volatility active</span>
            <button className="PS__hv-notice-off" onClick={() => onVolatilityChange("normal")}>Switch to Normal</button>
          </div>
        )}
      </div>

      {/* ══════ STICKY BOTTOM CTA ══════ */}
      <div className="PS__cta">

        {/* ── Gift + Promo row — inside CTA so never covered ── */}
        <div className="PS__extras-row">
          <motion.button
            className={`PS__gift-btn${giftMode ? " active" : ""}`}
            style={giftMode ? { "--gift-color": tier.accent } as React.CSSProperties : undefined}
            onClick={() => onGiftModeChange(!giftMode)}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }}
          >
            <span className="PS__gift-btn-icon">🎁</span>
            <span className="PS__gift-btn-label">{giftMode ? "Gifting" : "Gift"}</span>
          </motion.button>
          <div className={`PS__promo-wrap${promoDiscount > 0 ? " valid" : promoCode ? " invalid" : ""}`}>
            <span className="PS__promo-tag">🏷</span>
            <input
              className="PS__promo-field"
              placeholder="Promo code"
              value={promoCode}
              onChange={(e) => onPromoCodeChange(e.target.value.toUpperCase())}
              spellCheck={false}
            />
            {promoCode && promoDiscount > 0 && (
              <span className="PS__promo-save" style={{ background: tier.accent }}>-{(promoDiscount / 100).toFixed(0)}%</span>
            )}
            {promoCode && promoDiscount === 0 && (
              <span className="PS__promo-save invalid">✗</span>
            )}
          </div>
        </div>

        {/* Gift recipient address (expands below extras row) */}
        <AnimatePresence>
          {giftMode && (
            <motion.div className="PS__gift-addr-wrap"
              initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
            >
              <div className="PS__gift-addr-inner">
                <span className="PS__gift-addr-label">Recipient</span>
                <input
                  className={`PS__gift-addr-input${giftRecipient && !/^0x[0-9a-fA-F]{40}$/.test(giftRecipient) ? " err" : ""}`}
                  placeholder="0x… wallet address"
                  value={giftRecipient}
                  onChange={(e) => onGiftRecipientChange(e.target.value)}
                  spellCheck={false}
                />
                {giftRecipient && /^0x[0-9a-fA-F]{40}$/.test(giftRecipient) && (
                  <span className="PS__gift-addr-ok">✓</span>
                )}
              </div>
              {giftRecipient && !/^0x[0-9a-fA-F]{40}$/.test(giftRecipient) && (
                <p className="PS__gift-addr-err">Enter a valid 0x address</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Token + buy row */}
        <div className="PS__cta-row">
        {/* Token selector pill — always shows the TOTAL for the selected quantity */}
        <button className="PS__token-pill" onClick={() => setShowTokens(true)}>
          <span className="PS__token-pill-icon">{TOKENS[selectedToken].icon}</span>
          <span className="PS__token-pill-body">
            <span className="PS__token-pill-amount">
              {packPrices[selectedToken] != null
                ? formatPackPrice(packPrices[selectedToken]! * BigInt(packCount), selectedToken)
                : "…"}
            </span>
            <span className="PS__token-pill-usd">
              ≈ ${(usdPrice * packCount * (promoDiscount > 0 ? (10000 - promoDiscount) / 10000 : 1)).toLocaleString(undefined, {maximumFractionDigits: 0})} · {TOKENS[selectedToken].symbol} ⌄
            </span>
          </span>
        </button>

        {/* Main action */}
        {demo ? (
          <motion.button
            className="PS__buy-btn"
            onClick={onOpen}
            disabled={isOpening}
            style={{ "--btn-color": tier.accent, "--btn-glow": tier.foilColor } as React.CSSProperties}
            whileHover={!isOpening ? { scale: 1.015 } : {}} whileTap={!isOpening ? { scale: 0.975 } : {}}
          >
            {isOpening ? (
              <><Spinner /> {packCount > 1 ? `Opening ${packCount}…` : "Opening…"}</>
            ) : (
              `Open ${packCount > 1 ? `${packCount}× ` : ""}Demo Pack · Free`
            )}
          </motion.button>
        ) : !account ? (
          <button className="PS__buy-btn PS__buy-btn--connect">
            Connect Wallet
          </button>
        ) : tierSoldOut ? (
          <div className="PS__buy-btn PS__buy-btn--soldout">🚫 Sold Out</div>
        ) : needsApproval && selectedToken !== 3 ? (
          <motion.button
            className="PS__buy-btn"
            onClick={onApprove}
            disabled={isApproving}
            style={{ "--btn-color": "#4ade80", "--btn-glow": "rgba(74,222,128,0.35)" } as React.CSSProperties}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
          >
            {isApproving ? <><Spinner /> Approving…</> : `Approve ${TOKEN_SYMBOLS[selectedToken]}`}
          </motion.button>
        ) : giftMode && giftRecipient && !/^0x[0-9a-fA-F]{40}$/.test(giftRecipient) ? (
          <div className="PS__buy-btn" style={{ opacity: 0.45, cursor: "not-allowed", background: "rgba(255,255,255,0.06)" }}>
            Enter valid address
          </div>
        ) : (
          <motion.button
            className={`PS__buy-btn${!hasBalance && !isOpening ? " PS__buy-btn--low-bal" : ""}`}
            onClick={onOpen}
            disabled={isOpening}
            style={{ "--btn-color": tier.accent, "--btn-glow": tier.foilColor } as React.CSSProperties}
            whileHover={!isOpening ? { scale: 1.015 } : {}} whileTap={!isOpening ? { scale: 0.975 } : {}}
          >
            {isOpening ? (
              <><Spinner /> {packCount > 1 ? `Opening ${packCount}…` : "Opening…"}</>
            ) : giftMode && giftRecipient ? (
              `🎁 Gift ${packCount > 1 ? `${packCount}× ` : ""}· ${formatPackPrice(packPrices[selectedToken] != null ? packPrices[selectedToken]! * BigInt(packCount) : undefined, selectedToken)}`
            ) : packCount > 1 ? (
              `Open ${packCount}× · ${formatPackPrice(
                packPrices[selectedToken] != null ? packPrices[selectedToken]! * BigInt(packCount) : undefined,
                selectedToken
              )}`
            ) : (
              `Open Pack · ${formatPackPrice(packPrices[selectedToken], selectedToken)}`
            )}
          </motion.button>
        )}
        </div>{/* end PS__cta-row */}
      </div>{/* end PS__cta */}
    </div>
  );
}

// ── 3D CSS Pack Bag ───────────────────────────────────────────────────────────
export function Pack3D({ tier, active }: { tier: typeof TIER_DATA[number]; active: boolean }) {
  return (
    <motion.div
      className="Pack3D"
      whileHover={active ? { rotateY: 8, rotateX: -4, scale: 1.04 } : {}}
      transition={{ type: "spring", stiffness: 200, damping: 18 }}
      style={{
        "--pack-bg-top": tier.bgTop,
        "--pack-bg-mid": tier.bgMid,
        "--pack-stripe": tier.stripeColor,
        "--pack-crimp":  tier.crimpColor,
        "--pack-foil":   tier.foil,
      } as React.CSSProperties}
    >
      {/* Top crimp */}
      <div className="Pack3D__crimp top" />

      {/* Main body */}
      <div className="Pack3D__body">
        {/* Foil shimmer */}
        <div className="Pack3D__foil" />
        {/* Diagonal scratch lines */}
        <div className="Pack3D__scratches" />

        {/* BESC Draw logo area */}
        <div className="Pack3D__logo-area">
          <div className="Pack3D__brand-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill={tier.stripeColor} stroke={tier.stripeColor} strokeWidth="0.5" />
            </svg>
          </div>
          <div className="Pack3D__collectibles-badge" style={{ background: tier.stripeColor }}>
            BESC DRAW
          </div>
        </div>

        {/* Pack name embossed */}
        <div className="Pack3D__pack-name">{tier.name.toUpperCase()}</div>

        {/* Pokeball */}
        <div className="Pack3D__pokeball">
          <div className="Pack3D__pokeball-top" style={{ background: tier.stripeColor }} />
          <div className="Pack3D__pokeball-band" />
          <div className="Pack3D__pokeball-bottom" />
          <div className="Pack3D__pokeball-btn" />
        </div>

        {/* Bottom label */}
        <div className="Pack3D__bottom-label" style={{ color: "rgba(255,255,255,0.85)" }}>
          PICK 1
        </div>
      </div>

      {/* Bottom crimp */}
      <div className="Pack3D__crimp bottom" />

      {/* Selection glow */}
      {active && (
        <div className="Pack3D__glow" style={{ "--glow-color": tier.stripeColor } as React.CSSProperties} />
      )}
    </motion.div>
  );
}

// ── Pack icon (small, for modals) ─────────────────────────────────────────────
function PackIcon({ tier, size = 48 }: { tier: typeof TIER_DATA[number]; size?: number }) {
  return (
    <div className="PS__pack-icon" style={{ width: size, height: size * 1.3, background: `linear-gradient(160deg, ${tier.bgTop}, ${tier.bgMid})`, border: `2px solid ${tier.stripeColor}40` }}>
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill={tier.stripeColor} />
      </svg>
    </div>
  );
}

function Spinner() {
  return <span className="PS__spinner" />;
}
